import React from 'react';
import {useTranslation} from 'react-i18next';
import {MapContainer, Marker, Popup, TileLayer} from 'react-leaflet';
import {useNavigate} from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {PlaceListing} from '../../admin/artisticobjects/types/Place';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface PlacesMapProps {
  places: PlaceListing[];
}

const PlacesMap: React.FC<PlacesMapProps> = ({ places }) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const placesWithCoordinates = places.filter(
    place => place.address?.latitude != null && place.address?.longitude != null
  );

  if (placesWithCoordinates.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">{t('places.map.noCoordinates')}</p>
      </div>
    );
  }

  const bounds = placesWithCoordinates.map(place => [
    place.address!.latitude!,
    place.address!.longitude!
  ] as [number, number]);

  const center: [number, number] = bounds.length === 1
    ? bounds[0]
    : [
      bounds.reduce((sum, coord) => sum + coord[0], 0) / bounds.length,
      bounds.reduce((sum, coord) => sum + coord[1], 0) / bounds.length
    ];

  const handleMarkerClick = (placeId: string) => {
    navigate(`/gallery/place/${placeId}`);
  };

  return (
    <div className="h-full w-full">
      <MapContainer
        center={center}
        zoom={bounds.length === 1 ? 15 : 10}
        scrollWheelZoom={true}
        className="h-full w-full"
        bounds={bounds.length > 1 ? bounds : undefined}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {placesWithCoordinates.map(place => (
          <Marker
            key={place.id}
            position={[place.address!.latitude!, place.address!.longitude!]}
          >
            <Popup>
              <div>
                <a
                  href={`/gallery/place/${place.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMarkerClick(place.id);
                  }}
                  className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                >
                  {place.name}
                </a>
                {place.address?.town && (
                  <div className="text-sm text-gray-600 mt-1">{place.address.town}</div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default PlacesMap;

